import { JSX } from 'react';
import { ControllerRenderProps, FieldErrors, FieldPath, FieldValues } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import styles from 'shared/components/deso-external-id-text-field/deso-external-id-text-field.module.scss';
import useDesoExternalIdTextField from 'shared/components/deso-external-id-text-field/hooks';

type Props<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> = {
  field: ControllerRenderProps<TFieldValues, TName>;
  errors: FieldErrors<TFieldValues>;
  isImported?: boolean;
  label?: string;
};

const DesoExternalIdTextField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  field,
  errors,
  isImported,
  label,
}: Props<TFieldValues, TName>): JSX.Element => {
  const { formatExternalId } = useDesoExternalIdTextField();
  const DESO_TRANSMITTER_PLACEHOLDER = 'xxxxx';

  const renderPlaceholderStyle = (
    field: ControllerRenderProps<TFieldValues, TName>,
  ): {
    input: {
      disabled: boolean | undefined;
      startAdornment: JSX.Element;
    };
    htmlInput: {
      maxLength: number;
    };
  } => ({
    input: {
      disabled: isImported,
      startAdornment: (
        <span className={styles.placeHolderStyle}>
          <span className={styles.placeholderOpacity}>{field.value ?? ''}</span>
          <span>{DESO_TRANSMITTER_PLACEHOLDER.slice((field.value ?? '').length)}</span>
        </span>
      ),
    },
    htmlInput: {
      maxLength: 5,
    },
  });

  return (
    <TextField
      {...field}
      onChange={(e) => field.onChange(formatExternalId(e.target.value))}
      slotProps={renderPlaceholderStyle(field)}
      variant="outlined"
      id="externalIdInput"
      label={label}
      error={!!errors.externalId}
      helperText={typeof errors.externalId?.message === 'string' ? errors.externalId.message : undefined}
      className="form-field"
    />
  );
};

export default DesoExternalIdTextField;
